<script setup>
import { computed, onMounted, onUnmounted, ref, watch, watchEffect } from 'vue'
import { useAppStore } from '@/stores/app'
import { useGeoStore } from '@/stores/geo'
import { useRoute, useRouter } from 'vue-router'
import { area, centroid } from '@turf/turf'

import ProjectTabs from '@/pages/Project/Components/ProjectTabs.vue'
import SelectedAreaChip from '@/pages/Project/Components/SelectedAreaChip.vue'
import StatCard from './StatCard.vue'
import InsightCard from './InsightCard.vue'
import WeatherSection from './WeatherSection.vue'
import MapMonitoringFrame from '@/components/map/MapMonitoringFrame.vue'
import { useMonitoring, METRICS, DAY, RANGE_DAYS } from '@/composables/useMonitoring'
import { useSelectedArea } from '@/composables/useAreaSelection'
import { setMetricOverlay, clearMetricOverlay } from '@/composables/useMetricPaint'
import { buildInsights } from '@/utils/monitoring/insights'
import { getWeather } from '@/utils/monitoring/weather'
import { CHOROPLETH_DEFS } from '@/utils/monitoring/colormaps'

const appStore = useAppStore()

const geoStore = useGeoStore()
const route = useRoute()
const router = useRouter()

const orgId = computed(() => route.params.org_id)
const projectId = computed(() => route.params.project_id)
const isLoggedIn = computed(() => appStore.isLoggedIn)

const {
  series,
  geoms,
  expanded,
  activeRes,
  featureValues,
  range,
  refreshAll,
  setResolution,
  startPolling,
  stopPolling,
  monitoredFor,
  failedFor,
} = useMonitoring(orgId, projectId)

onMounted(() => startPolling())
onUnmounted(() => {
  stopPolling()
  clearMetricOverlay()
})

watch(isLoggedIn, (v) => {
  if (v) refreshAll()
}, { immediate: true })
watch(projectId, (n, old) => {
  if (n && n !== old) {
    setExpanded(null)
    refreshAll()
  }
})

// Line colors sampled from the same Planet PV colormaps as the overlays
// (Blues / hot / YlGn / viridis / RdYlGn).
const COLORS = {
  swc: '#2d7dbb',
  lst: '#ff3900',
  ndvi: '#3faa59',
  biomass: '#4bb062',
  canopyCover: '#228342',
  canopyHeight: '#066d39',
  carbonDensity: '#22a784',
}

const seriesByKey = computed(() =>
  Object.fromEntries(
    METRICS.map((m) => [
      m.key,
      series[m.key].points.map((p) => ({ x: Date.parse(p.date), y: p.mean })),
    ])
  )
)

const cards = computed(() =>
  METRICS.map((m) => {
    const s = series[m.key]
    const pts = seriesByKey.value[m.key]
    const last = pts[pts.length - 1]
    return {
      ...m,
      color: COLORS[m.key],
      // A monitored metric with no points yet is still being processed
      // upstream — syncing. But when every variant behind it has failed, say
      // so instead of syncing forever.
      state: pts.length
        ? 'live'
        : failedFor(m.key)
          ? 'error'
          : (s.loading || monitoredFor(m.key).length ? 'loading' : 'empty'),
      value: last ? last.y.toFixed(m.dp) : '',
    }
  })
)

// --- Shared time axis: every chart renders on the same domain (the longest
// series wins); the range pill narrows the visible window without refetching.
const globalMin = computed(() => {
  const firsts = METRICS.map((m) => seriesByKey.value[m.key][0]?.x).filter((x) => x != null)
  return firsts.length ? Math.min(...firsts) : Date.now() - 365 * DAY
})

const xDomain = computed(() => {
  const max = Date.now()
  const min = range.value === 'all' ? globalMin.value : max - RANGE_DAYS[range.value] * DAY
  return { min, max }
})

const allLabel = computed(() => {
  const years = Math.max(1, Math.round((Date.now() - globalMin.value) / (365 * DAY)))
  return `All · ${years}y`
})

const RANGE_BUTTONS = [
  { key: 'all', label: '' },
  { key: '1y', label: '1Y' },
  { key: '1m', label: '1M' },
]

const rangeLabel = (points) => {
  if (points.length < 2) return ''
  const span = points[points.length - 1].x - points[0].x
  const fmt = (ms) =>
    span > 3 * 365 * DAY
      ? String(new Date(ms).getFullYear())
      : new Date(ms).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  return `${fmt(points[0].x)} – ${fmt(points[points.length - 1].x)}`
}

// Data = raw metric grid, Insights = composite insight cards. Leaving Data
// collapses any expanded card so its map overlays don't linger.
const view = ref('data')
watch(view, (v) => {
  if (v === 'insights') setExpanded(null)
})

// --- Expanded card <-> ?metric= deep link
const setExpanded = (key) => {
  if (expanded.value === key) return
  expanded.value = key
  const query = { ...route.query }
  if (key) query.metric = key
  else delete query.metric
  router.replace({ query })
}

watch(
  () => route.query.metric,
  (m) => {
    const key = METRICS.some((x) => x.key === m) ? m : null
    if (key !== expanded.value) expanded.value = key
    if (key) view.value = 'data'
  },
  { immediate: true }
)

const variantTabs = (metric) => {
  const av = series[metric].available || []
  if (av.length < 2) return null
  return av.map((r) => ({ key: String(r), label: String(r) === '1000' ? '1km' : `${r}m` }))
}

const activeVariant = (metric) => String(activeRes[metric] ?? series[metric].resolution ?? '')

// Chart hover = time slider: the hovered date's raster overlays the features
// and persists after the cursor leaves. Debounced — dragging across a dense
// series shouldn't remount tile layers hundreds of times.
const scrubDate = ref({})
const hoverDate = ref(null)
// Frozen colormap domain of the displayed raster, when every feature agrees.
const frameDomain = ref(null)
let scrubTimer = null
const onScrub = (metric, date) => {
  if (scrubDate.value[metric] === date) return
  scrubDate.value = { ...scrubDate.value, [metric]: date }
  clearTimeout(scrubTimer)
  scrubTimer = setTimeout(() => {
    hoverDate.value = date
  }, 200)
}
watch(expanded, () => {
  scrubDate.value = {}
  hoverDate.value = null
  frameDomain.value = null
})

const selectedArea = useSelectedArea(projectId)

// Selection scopes the monitored set everywhere below: raster frames, the
// choropleth's pending codes, weather centroid and total area.
const frameCodes = computed(() => {
  if (!expanded.value) return []
  const codes = monitoredFor(expanded.value).map((r) => r.feature_code)
  const sel = selectedArea.value?.code
  return sel ? codes.filter((c) => c === sel) : codes
})

// The expanded metric tints the project feature layers themselves, so the
// raster can sit between their fill and outline instead of under both.
watchEffect(() => {
  if (!expanded.value) {
    clearMetricOverlay()
    return
  }
  setMetricOverlay({
    groupId: `project-${projectId.value}`,
    metric: expanded.value,
    values: { ...featureValues.value },
    pending: frameCodes.value,
  })
})

const scopedGeoms = computed(() => {
  const sel = selectedArea.value?.code
  return sel && geoms[sel] ? { [sel]: geoms[sel] } : geoms
})

// --- Weather at the first monitored feature's centroid (map center fallback)
const mapReady = computed(() => geoStore.mapReady)
const weatherCoords = ref(null)

const resolveWeatherCoords = () => {
  const g = Object.values(scopedGeoms.value)[0]
  if (g) {
    try {
      const [lon, lat] = centroid(g).geometry.coordinates
      weatherCoords.value = { lat, lon }
      return
    } catch (e) {
      // fall through to map center
    }
  }
  const map = geoStore.map
  if (map && mapReady.value) {
    const c = map.getCenter()
    weatherCoords.value = { lat: c.lat, lon: c.lng }
  }
}

watch(
  [() => Object.keys(geoms).length, () => selectedArea.value?.code, mapReady],
  resolveWeatherCoords,
  { immediate: true }
)

const weather = ref(null)
watchEffect(() => {
  const c = weatherCoords.value
  weather.value = c ? getWeather(c.lat, c.lon) : null
})

// --- Insights
const areaHa = computed(() =>
  Object.values(scopedGeoms.value).reduce((sum, g) => {
    try {
      return sum + area(g) / 10_000
    } catch (e) {
      return sum
    }
  }, 0)
)

const insights = computed(() => {
  const raw = Object.fromEntries(
    METRICS.map((m) => [m.key, series[m.key].points.length ? series[m.key].points : null])
  )
  const windowMs = range.value === 'all' ? null : RANGE_DAYS[range.value] * DAY
  return buildInsights(raw, weather.value, areaHa.value, windowMs, Date.now())
})

const insightSeries = (spark) =>
  (spark ?? []).map((s) => ({
    label: s.label,
    color: COLORS[s.metric] ?? '#888',
    points: s.points,
  }))
</script>

<template>
  <ProjectTabs />
  <SelectedAreaChip />

  <div class="p-3 space-y-5">
    <section>
      <div class="mb-2 flex items-center justify-between">
        <div class="flex gap-0.5 rounded-lg bg-muted p-0.5">
          <button
            v-for="v in ['data', 'insights']"
            :key="v"
            class="rounded-md px-2 py-0.5 text-sm font-medium transition-colors"
            :class="view === v ? 'bg-background shadow font-semibold' : 'text-muted-foreground hover:text-foreground'"
            @click="view = v"
          >
            {{ v === 'data' ? 'Data' : 'Insights' }}
          </button>
        </div>
        <div class="flex gap-0.5 rounded-lg bg-muted p-0.5">
          <button
            v-for="r in RANGE_BUTTONS"
            :key="r.key"
            class="rounded-md px-2 py-0.5 text-sm font-medium transition-colors"
            :class="range === r.key ? 'bg-background shadow font-semibold' : 'text-muted-foreground hover:text-foreground'"
            @click="range = r.key"
          >
            {{ r.key === 'all' ? allLabel : r.label }}
          </button>
        </div>
      </div>

      <div v-if="view === 'data'" class="grid grid-cols-2 gap-2">
        <template v-for="c in cards" :key="c.key">
          <div v-if="c.state === 'loading'" class="rounded-lg border bg-card p-3">
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium text-muted-foreground truncate">{{ c.title }}</span>
              <span class="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#fab219]/15 px-1.5 py-0.5 text-xs font-semibold text-[#8a5a00]">
                <span class="size-1.5 animate-pulse rounded-full bg-[#fab219]"></span> syncing
              </span>
            </div>
            <div class="mt-2 h-5 w-16 rounded bg-muted animate-pulse"></div>
            <div class="mt-2 h-8 rounded bg-muted animate-pulse"></div>
          </div>
          <div v-else-if="c.state === 'error'" class="rounded-lg border bg-card p-3">
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium text-muted-foreground truncate">{{ c.title }}</span>
              <span class="inline-flex shrink-0 items-center gap-1 rounded-full bg-destructive/10 px-1.5 py-0.5 text-xs font-semibold text-destructive">
                unavailable
              </span>
            </div>
            <div class="mt-1 text-xl font-bold leading-none text-muted-foreground">—</div>
            <div class="mt-2 text-xs text-muted-foreground">This data source isn't responding — we're on it</div>
          </div>
          <div v-else-if="c.state === 'empty'" class="rounded-lg border bg-card p-3 opacity-70">
            <div class="text-sm font-medium text-muted-foreground truncate">{{ c.title }}</div>
            <div class="mt-1 text-xl font-bold leading-none text-muted-foreground">—</div>
            <div class="mt-2 text-xs text-muted-foreground">No data yet</div>
          </div>
          <StatCard
            v-else
            :title="c.title"
            :value="c.value"
            :unit="c.unit"
            :color="c.color"
            :points="seriesByKey[c.key]"
            :x-min="xDomain.min"
            :x-max="xDomain.max"
            :map-legend="CHOROPLETH_DEFS[c.key]"
            :domain="expanded === c.key ? frameDomain : null"
            :range-text="rangeLabel(seriesByKey[c.key])"
            :variants="variantTabs(c.key)"
            :active-variant="activeVariant(c.key)"
            :scrub-date="scrubDate[c.key] ?? null"
            :expanded="expanded === c.key"
            @toggle="setExpanded(expanded === c.key ? null : c.key)"
            @variant="(res) => setResolution(c.key, res)"
            @scrub="(date) => onScrub(c.key, date)"
          />
        </template>
      </div>

      <div v-else class="space-y-2">
        <template v-for="c in insights" :key="c.key">
          <div v-if="c.waiting" class="rounded-lg border bg-card p-3">
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium text-muted-foreground truncate">{{ c.title }}</span>
              <span class="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-[#fab219]/15 px-1.5 py-0.5 text-xs font-semibold text-[#8a5a00]">
                <span class="size-1.5 animate-pulse rounded-full bg-[#fab219]"></span> syncing
              </span>
            </div>
            <div class="mt-2 h-5 w-2/3 rounded bg-muted animate-pulse"></div>
            <div class="mt-2 h-8 rounded bg-muted animate-pulse"></div>
          </div>
          <InsightCard
            v-else
            :title="c.title"
            :status="c.status"
            :headline="c.headline"
            :details="c.details"
            :series="insightSeries(c.spark)"
            :x-min="xDomain.min"
            :x-max="xDomain.max"
          />
        </template>
      </div>
    </section>

    <WeatherSection :stats="weather" />
  </div>

  <MapMonitoringFrame
    v-if="expanded"
    :org-id="orgId"
    :project-id="projectId"
    :metric="expanded"
    :resolution="activeRes[expanded] ? String(activeRes[expanded]) : null"
    :date="hoverDate"
    :codes="frameCodes"
    :geometries="geoms"
    @domain="frameDomain = $event"
  />
</template>
